import React, { useState, useMemo, useRef, useEffect } from 'react';
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import 'quill-mention';
import './mention.css';
import axiosInstance from '../../services/AxiosInstance';
import TaskAttachmentImage from '../components/TaskAttachmentImage';
import { openTaskAttachmentInNewTab } from '../../utils/taskAttachment';
import swal from "sweetalert";
import { toast } from 'react-toastify';
import { formatDateTimeToDDMMMYYYY } from '../../utils/dateFormatter';
import WorkRegisterTagModal from '../workRegister/WorkRegisterTagModal';
import { useHasMenuKey } from '../../hooks/useHasMenuKey';

// Custom Quill module for image deletion
const ImageResize = Quill.import('modules/imageResize');

// Display subject when empty: first line or first 10 words from content (display only, not stored)
const getDisplaySubject = (subject, content) => {
  if (subject && String(subject).trim()) return String(subject).trim();
  const plain = (content || '').replace(/<[^>]+>/g, ' ').replace(/&nbsp;/gi, ' ').replace(/\s+/g, ' ').trim();
  const firstLine = plain.split(/\n/)[0] || plain;
  const words = firstLine.split(/\s+/).filter(Boolean).slice(0, 10).join(' ');
  return words || '(No subject)';
};

export default function CommentSection({ users = [], comments = [], onCommentAdded, taskid, scrollToCommentId }) {
  const { hasKey: hasWorkRegisterAccess } = useHasMenuKey('work_register');
  const quillRef = useRef(null);
  const subjectInputRef = useRef(null);
  const [expanded, setExpanded] = useState(false);
  const [editorContent, setEditorContent] = useState('');
  const [subject, setSubject] = useState('');
  const [mode, setMode] = useState('comment');
  const [activeCommentId, setActiveCommentId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [likes, setLikes] = useState({});
  const [parent_id, setParent_id] = useState();
  const [files, setFiles] = useState();
  const [mentions, setMentions] = useState([])
  const [combinedComments, setCombinedComments] = useState([]);
  const [submittedObject, setSubmittedObject] = useState({})
  const [uploadedFile, setUploadedFile] = useState([]);
  const uploadedFileRef = useRef([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [replyingTo, setReplyingTo] = useState(null);
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isReplying, setIsReplying] = useState(false);
  const [isDeletingFile, setIsDeletingFile] = useState(false);
  const [commentError, setCommentError] = useState('');
  const [wrTagCommentId, setWrTagCommentId] = useState(null);

  // Get current user from localStorage
  useEffect(() => {
    const userData = localStorage.getItem("user");
    if (userData) {
      const user = JSON.parse(userData);
      setCurrentUser(user);
    }
  }, []);

  // Custom confirmation dialog function (ESLint compliant)
  const showConfirmDialog = (message) => {
    return window.confirm ? window.confirm(message) : true; // Fallback for environments without confirm
  };

  useEffect(() => {
    const processComments = (commentList) => {
      return commentList.map(comment => {
        let user = users.find(u => u.id === comment.user_id);

        if (!user && comment.CorporateUser) {
          user = {
            id: comment.user_id,
            full_name: comment.CorporateUser.full_name || comment.CorporateUser.User?.name || comment.CorporateUser.User?.email || "Unknown",
            Email: comment.CorporateUser.User?.email || "",
            DepartmentName: ""
          };
        }

        let fullContent = comment.content || "";
        let displayContent = comment.content || "";

        const processedComment = {
          commentId: comment.id,
          subject: getDisplaySubject(comment.subject, comment.content),
          originalSubject: comment.subject || '',
          content: displayContent,
          fullContent: fullContent, // ✅ Full content with images and delete buttons for editing
          plainText: (comment.content || '').replace(/<[^>]+>/g, ""),
          createdAt: formatCommentDate(comment.created_at),
          updatedAt: formatCommentDate(comment.updated_at),
          likes: comment.likes,
          parentId: comment.parent_id,
          userId: comment.user_id,
          userName: user?.full_name || "Unknown",
          userEmail: user?.Email || "",
          department: user?.DepartmentName || "",
          taskid: comment.TaskId,
          files: comment.files || []
        };

        if (comment.replies && comment.replies.length > 0) {
          processedComment.replies = processComments(comment.replies);
        }

        return processedComment;
      });
    };

    const merged = processComments(comments);
    setCombinedComments(merged);
  }, [comments, users]);

  // Scroll to and highlight comment/reply when navigating with #comment-{id}
  useEffect(() => {
    if (!scrollToCommentId || combinedComments.length === 0) return;

    const scrollTimer = setTimeout(() => {
      const el = document.getElementById(`comment-${scrollToCommentId}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.classList.add('comment-highlight');
        setTimeout(() => el.classList.remove('comment-highlight'), 2000);
      }
    }, 250);

    return () => clearTimeout(scrollTimer);
  }, [scrollToCommentId, combinedComments]);

  // ✅ Enhanced file handling with wrapper and delete button for all file types
  const wrapFilesWithDeleteButton = (content) => {
    const div = document.createElement('div');
    div.innerHTML = content;

    // Handle images
    const images = div.querySelectorAll('img:not(.file-wrapper img)');
    images.forEach(img => {
      if (!img.closest('.file-wrapper')) {
        const wrapper = document.createElement('div');
        wrapper.className = 'file-wrapper';
        wrapper.setAttribute('data-file-type', 'image');

        const deleteBtn = document.createElement('button');
        deleteBtn.className = 'delete-btn';
        deleteBtn.onclick = (e) => {
          e.preventDefault();
          e.stopPropagation();
          if (showConfirmDialog('Remove this file?')) {
            wrapper.remove();
            const quill = quillRef.current?.getEditor();
            if (quill) {
              setEditorContent(quill.root.innerHTML);
            }
          }
        };

        img.parentNode.insertBefore(wrapper, img);
        wrapper.appendChild(img);
        wrapper.appendChild(deleteBtn);
      }
    });

    // Handle other file links
    const links = div.querySelectorAll('a:not(.file-wrapper a)');
    links.forEach(link => {
      if (!link.closest('.file-wrapper') && link.href) {
        const wrapper = document.createElement('div');
        wrapper.className = 'file-wrapper';

        // Try to determine file type from URL or text
        const fileName = link.textContent || link.href.split('/').pop() || 'file';
        const fileExtension = fileName.split('.').pop()?.toLowerCase() || 'unknown';
        wrapper.setAttribute('data-file-type', fileExtension);

        // Style the link
        link.className = 'file-link';
        const icon = document.createElement('div');
        icon.className = 'file-icon';
        link.insertBefore(icon, link.firstChild);

        const deleteBtn = document.createElement('button');
        deleteBtn.className = 'delete-btn';
        deleteBtn.onclick = (e) => {
          e.preventDefault();
          e.stopPropagation();
          if (showConfirmDialog('Remove this file?')) {
            wrapper.remove();
            const quill = quillRef.current?.getEditor();
            if (quill) {
              setEditorContent(quill.root.innerHTML);
            }
          }
        };

        link.parentNode.insertBefore(wrapper, link);
        wrapper.appendChild(link);
        wrapper.appendChild(deleteBtn);
      }
    });

    return div.innerHTML;
  };

  const handleFileUpload = () => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', '*/*');
    input.setAttribute('multiple', 'true');
    input.value = '';
    input.click();

    input.onchange = async () => {
      const files = Array.from(input.files || []);
      if (files.length === 0) return;

      setUploadedFile(prev => {
        const updated = [...(prev || []), ...files];
        uploadedFileRef.current = updated;
        return updated;
      });
    };
  };

  const handleRemoveFile = (index) => {
    setUploadedFile(prev => {
      const updated = prev.filter((_, i) => i !== index);
      uploadedFileRef.current = updated;
      return updated;
    });
  };

  const handleRemoveExistingFile = async (fileId, fileName) => {
    const willDelete = await swal({
      title: "Are you sure?",
      text: `Do you want to remove "${fileName}" from this comment?`,
      icon: "warning",
      buttons: true,
      dangerMode: true,
    });

    if (willDelete) {
      setIsDeletingFile(true);
      try {
        let result = await axiosInstance.post("/task/deletecommentfile", {
          commentId: activeCommentId,
          fileId: fileId
        });

        if (result) {
          // Remove the file from the uploadedFile state
          setUploadedFile(prev => {
            const updated = prev.filter(file => file.id !== fileId);
            uploadedFileRef.current = updated;
            return updated;
          });
          
          // Update the comment in the UI
          setCombinedComments(prev =>
            prev.map(comment =>
              comment.commentId === activeCommentId
                ? { ...comment, files: comment.files.filter(f => f.id !== fileId) }
                : comment
            )
          );
          
          swal("File removed successfully!", {
            icon: "success",
          });
        } else {
          notifyError("Error removing file");
        }
      } catch (err) {
        notifyError("An unexpected error occurred");
        console.error(err);
      } finally {
        setIsDeletingFile(false);
      }
    }
  };

  const mentionValues = useMemo(() => {
    return users.map((user) => ({
      id: user.id,
      value: user?.full_name,
    }));
  }, [users]);

  const modules = useMemo(() => ({
    toolbar: {
      container: [
        ['bold', 'italic', 'underline', 'strike'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ indent: '-1' }, { indent: '+1' }],
        [{ align: [] }]
      ]
    },
    mention: {
      mentionDenotationChars: ['@'],
      source: function (searchTerm, renderList) {
        const matches = mentionValues.filter((item) =>
          item.value.toLowerCase().includes(searchTerm.toLowerCase())
        );
        renderList(matches, searchTerm);
      },
      renderItem(item) {
        return `<div><strong>${item.value}</strong></div>`;
      },
    }
  }), [mentionValues]);

  const handleAddComment = () => {
    setMode('comment');
    setActiveCommentId(null);
    setReplyingTo(null);
    setParent_id(null);
    setSubject('');
    setEditorContent('');
    setUploadedFile([]);
    setExpanded(true);
    
    // Focus the subject field after a short delay to ensure it's rendered
    setTimeout(() => {
      subjectInputRef.current?.focus();
    }, 100);
  };

  function extractContentAndFiles(html) {
    const div = document.createElement('div');
    div.innerHTML = html;

    // Extract all files (images and other files)
    const files = [];

    // Extract images
    const images = Array.from(div.querySelectorAll('img')).map(img => img.getAttribute('src'));
    files.push(...images);

    // Extract other file links - only data URLs (base64 files)
    const fileLinks = Array.from(div.querySelectorAll('a[href]'))
      .map(link => link.getAttribute('href'))
      .filter(href => href && href.startsWith('data:'));
    files.push(...fileLinks);

    // Remove file wrappers and their contents
    div.querySelectorAll('.file-wrapper').forEach(wrapper => wrapper.remove());

    // Also remove any standalone images and links that weren't wrapped
    div.querySelectorAll('img').forEach(img => img.remove());
    div.querySelectorAll('a[href]').forEach(link => {
      // Only remove if it looks like a file link (data URLs)
      if (link.href.startsWith('data:')) {
        link.remove();
      }
    });

    const cleanHtml = div.innerHTML.trim();

    return {
      content: cleanHtml,
      files: files.filter(file => file && file.trim() !== '') // Remove empty entries
    };
  }

  function base64ToFile(base64, filename) {
    const arr = base64.split(',');
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, { type: mime });
  }

  const addComment = async () => {
    setIsSubmitting(true);
    const formData = new FormData();
    let userdata = JSON.parse(localStorage.getItem("user"));
    const { content, files } = extractContentAndFiles(editorContent);
    const contentFile = files || uploadedFile;
    formData.append('corporate_id', userdata?.corporateId);
    formData.append('user_id', userdata?.id);
    formData.append('content', content);
    if (subject.trim()) formData.append('subject', subject.trim());
    formData.append('taskid', taskid);
    formData.append('parent_id', parent_id ?? 0);
    formData.append('mentions', JSON.stringify(mentions ?? []));
    formData.append('created_at', new Date().toISOString());
    formData.append('updated_at', new Date().toISOString());

    if (files && files.length > 0) {
      files.forEach((src, index) => {
        if (src.startsWith("data:")) {
          // Extract MIME type from base64
          const mimeMatch = src.match(/data:([^;]+)/);
          const mimeType = mimeMatch ? mimeMatch[1] : 'application/octet-stream';
          const extension = mimeType.split('/')[1] || 'bin';

          // Use correct extension instead of hardcoding .png
          const file = base64ToFile(src, `file_${Date.now()}_${index}.${extension}`);
          formData.append('files', file);
        }
      });
    } else if (uploadedFile && uploadedFile.length > 0) {
      uploadedFile.forEach((src, index) => {
        formData.append('files', src);
      });
    }

    const submittedObject = Object.fromEntries(formData.entries());

    try {
      const response = await axiosInstance.post('/task/addcomment', formData);

      const result = response?.data;
      if (result?.success === false) {
        toast.error(result.message || result.error || 'Could not add comment');
        return result;
      }
      if (result?.comment) {
        const comment = result.comment;
        addCommentToList({
          id: comment.id,
          user_id: userdata?.id,
          content: comment.content,
          subject: subject.trim(),
          created_at: comment.created_at,
          updated_at: comment.updated_at,
          parent_id: parent_id || 0,
          taskid
        });
        setExpanded(false);
        setSubject('');
        setEditorContent('');
        setUploadedFile([]);
        if (onCommentAdded) {
          onCommentAdded();
        }
      }
      return result;
    } catch (error) {
      console.error('Error:', error);
      const msg = error?.response?.data?.message || error?.response?.data?.error || error?.message;
      if (msg) toast.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const addCommentToList = (submittedObject) => {
    let data = submittedObject;
    const user = users.find(u => u.id == data.user_id) || {};

    const newComment = {
      commentId: data.id,
      subject: getDisplaySubject(data.subject, data.content),
      originalSubject: data.subject || "",
      content: data.content,
      fullContent: data.content,
      plainText: data.content.replace(/<[^>]+>/g, ""),
      createdAt: formatCommentDate(data.created_at),
      updatedAt: formatCommentDate(data.updated_at),
      likes: data.likes,
      parentId: data.parent_id,
      userId: data.user_id,
      userName: user?.full_name || "Unknown",
      userEmail: user.Email || "",
      department: user.DepartmentName || "",
      taskid: data.taskid,
      files: []
    };

    if (data.parent_id && data.parent_id !== 0) {
      setCombinedComments(prevComments => {
        const addReplyToOriginalComment = (comments) => {
          return comments.map(comment => {
            if (comment.commentId === data.parent_id) {
              return {
                ...comment,
                replies: [...(comment.replies || []), newComment]
              };
            }
            if (comment.replies && comment.replies.length > 0) {
              return {
                ...comment,
                replies: addReplyToOriginalComment(comment.replies)
              };
            }
            return comment;
          });
        };
        return addReplyToOriginalComment(prevComments);
      });
    } else {
      setCombinedComments(prevComments => [...prevComments, newComment]);
    }
  };

  const handleReply = (comment) => {
    setMode('reply');
    setReplyingTo(comment.commentId);
    const originalCommentId = comment.parentId || comment.commentId;
    setActiveCommentId(originalCommentId);
    setParent_id(originalCommentId);
    setSubject('');
    setEditorContent(`<p><span style="background:#e0f7ff; color:#007bff;">@${comment.userName}</span>&nbsp;</p><p><br/></p>`);
    setUploadedFile([]);
    
    // Focus the subject field after a short delay to ensure it's rendered
    setTimeout(() => {
      subjectInputRef.current?.focus();
    }, 100);
  };

  // ✅ FIXED handleEdit function with file delete functionality for all file types
  const handleEdit = async (comment) => {
    setMode('edit');
    if (comment.parentId != null) {
      setEditingCommentId(comment.parentId);
    } else {
      setEditingCommentId(comment.commentId);
    }

    setActiveCommentId(comment.commentId);
    setSubject(comment.originalSubject ?? comment.subject ?? "");

    // ✅ Use fullContent and wrap all files with delete functionality
    const contentWithDeleteBtns = wrapFilesWithDeleteButton(comment.fullContent || comment.content || "");
    setEditorContent(contentWithDeleteBtns);
    
    // Load existing files for editing
    const existingFiles = comment.files || [];
    setUploadedFile(existingFiles);
    setExpanded(true);

    // Set up the global update function for this edit session
    setTimeout(() => {
      window.updateEditorContent = () => {
        const quill = quillRef.current?.getEditor();
        if (quill) {
          setEditorContent(quill.root.innerHTML);
        }
      };
      
      // Focus the subject field
      subjectInputRef.current?.focus();
    }, 100);
  };

  const handleEditSubmit = async () => {
    setIsEditing(true);
    const formData = new FormData();
    let userdata = JSON.parse(localStorage.getItem("user"));

    const { content, files } = extractContentAndFiles(editorContent);

    formData.append('corporate_id', userdata?.corporateId);
    formData.append('comment_id', activeCommentId);
    formData.append('user_id', userdata?.id);
    formData.append('content', content);
    formData.append('subject', subject.trim());
    formData.append('taskid', taskid);
    formData.append('parent_id', parent_id ?? 0);
    formData.append('mentions', JSON.stringify(mentions ?? []));
    formData.append('created_at', new Date().toISOString());
    formData.append('updated_at', new Date().toISOString());

    if (files && files.length > 0) {
      files.forEach((src, index) => {
        if (src.startsWith("data:image/")) {
          // Handle base64 images
          const file = base64ToFile(src, `image_${Date.now()}_${index}.png`);
          formData.append('files', file);
        } else if (src.startsWith("data:")) {
          // Handle other base64 files
          const mimeMatch = src.match(/data:([^;]+)/);
          const mimeType = mimeMatch ? mimeMatch[1] : 'application/octet-stream';
          const extension = mimeType.split('/')[1] || 'bin';
          const file = base64ToFile(src, `file_${Date.now()}_${index}.${extension}`);
          formData.append('files', file);
        }
      });
    } else if (uploadedFile && uploadedFile.length > 0) {
      // Separate existing files from new files
      const existingFiles = uploadedFile.filter(file => file.id !== undefined);
      const newFiles = uploadedFile.filter(file => file.id === undefined);
      
      // Add existing file IDs to preserve them
      if (existingFiles.length > 0) {
        formData.append('existing_files', JSON.stringify(existingFiles.map(f => f.id)));
      }
      
      // Add new files
      newFiles.forEach((file, index) => {
        formData.append('files', file);
      });
    }

    try {
      const response = await axiosInstance.put('/task/updatecomment', formData);
      const data = response.data;
      if (data?.success === false) {
        toast.error(data.message || data.error || 'Could not update comment');
        return;
      }
      if (data.message === "Comment updated successfully") {
        setCombinedComments(prev => {
          const updateComment = (comments) => {
            return comments.map(comment => {
              if (comment.commentId === activeCommentId) {
                return {
                  ...comment,
                  subject: getDisplaySubject(subject.trim() || comment.originalSubject, content),
                  originalSubject: subject.trim() || comment.originalSubject,
                  content,
                  fullContent: editorContent,
                  plainText: content.replace(/<[^>]+>/g, ""),
                  updatedAt: formatCommentDate(new Date()),
                };
              }
              if (comment.replies && comment.replies.length > 0) {
                return {
                  ...comment,
                  replies: updateComment(comment.replies)
                };
              }
              return comment;
            });
          };
          return updateComment(prev);
        });

        setMode('comment');
        setActiveCommentId(null);
        setSubject('');
        setEditorContent('');
        setExpanded(false);
        setEditingId(null);
        setUploadedFile([]);
        // Clean up global function
        window.updateEditorContent = null;
        // Refresh comment list so attachments and file ids match server after edit
        if (onCommentAdded) {
          onCommentAdded();
        }
      }
    } catch (error) {
      console.error("Error updating comment:", error);
      const msg = error?.response?.data?.message || error?.response?.data?.error || error?.message;
      if (msg) toast.error(msg);
    } finally {
      setIsEditing(false);
    }
  };

  const handleReplySubmit = async () => {
    setIsReplying(true);
    let userdata = JSON.parse(localStorage.getItem("user"));
    const { content, files } = extractContentAndFiles(editorContent);

    const formData = new FormData();
    formData.append('corporate_id', userdata?.corporateId);
    formData.append('user_id', userdata?.id);
    formData.append('content', content);
    if (subject.trim()) formData.append('subject', subject.trim());
    formData.append('taskid', taskid);
    formData.append('parent_id', activeCommentId ?? 0);
    formData.append('mentions', JSON.stringify(mentions ?? []));
    formData.append('created_at', new Date().toISOString());
    formData.append('updated_at', new Date().toISOString());

    if (files && files.length > 0) {
      files.forEach((src, index) => {
        if (src.startsWith("data:image/")) {
          // Handle base64 images
          const file = base64ToFile(src, `image_${Date.now()}_${index}.png`);
          formData.append('files', file);
        } else if (src.startsWith("data:")) {
          // Handle other base64 files
          const mimeMatch = src.match(/data:([^;]+)/);
          const mimeType = mimeMatch ? mimeMatch[1] : 'application/octet-stream';
          const extension = mimeType.split('/')[1] || 'bin';
          const file = base64ToFile(src, `file_${Date.now()}_${index}.${extension}`);
          formData.append('files', file);
        }
      });
    } else if (uploadedFile && uploadedFile.length > 0) {
      uploadedFile.forEach((src, index) => {
        formData.append('files', src);
      });
    }

    const submittedObject = Object.fromEntries(formData.entries());

    try {
      const response = await axiosInstance.post('/task/addcomment', formData);

      const result = response?.data;
      if (result?.success === false) {
        const msg = result.message || result.error;
        if (msg) toast.error(msg);
        return result;
      }
      if (result?.comment) {
        const comment = result.comment;
        addCommentToList({
          id: comment.id,
          user_id: userdata?.id,
          content: comment.content,
          subject: comment.subject || subject.trim() || "",
          created_at: comment.created_at,
          updated_at: comment.updated_at,
          parent_id: activeCommentId ?? 0,
          taskid
        });
      setMode('comment');
      setSubject('');
      setActiveCommentId(null);
      setReplyingTo(null);
      setParent_id(null);
      setEditorContent('');
      setExpanded(false);
      setUploadedFile([]);
        if (onCommentAdded) {
          onCommentAdded();
        }
      }
      return result;
    } catch (error) {
      console.error('Error:', error);
      const msg = error?.response?.data?.message || error?.response?.data?.error || error?.message;
      if (msg) toast.error(msg);
    } finally {
      setIsReplying(false);
    }
  };

  const handleSubmit = () => {
    const plainContent = (editorContent || '').replace(/<[^>]+>/g, ' ').replace(/&nbsp;/gi, ' ').replace(/\s+/g, ' ').trim();
    if (!plainContent) {
      setCommentError('Comment is required');
      return;
    }
    setCommentError('');
    if (mode === 'reply') {
      handleReplySubmit();
    } else if (mode === 'edit') {
      handleEditSubmit();
    } else {
      addComment();
    }
  };

  const handleCancel = () => {
    setCommentError('');
    setSubject('');
    setEditorContent('');
    setActiveCommentId(null);
    setReplyingTo(null);
    setParent_id(null);
    setMode('comment');
    setExpanded(false);
    setUploadedFile([]);
    // Clean up global function
    window.updateEditorContent = null;
  };

  const formatCommentDate = (dateStr) => {
    return formatDateTimeToDDMMMYYYY(dateStr);
  };

  const handleLike = (id) => {
    setLikes((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleChange = (content, _delta, _source, editor) => {
    setEditorContent(content);
    if (commentError) setCommentError('');

    const quillDelta = editor.getContents();
    const ops = quillDelta.ops;

    const mentions = [];
    let cursor = 0;

    ops.forEach(op => {
      if (typeof op.insert === 'string') {
        cursor += op.insert.length;
      } else if (op.insert?.mention) {
        const mentionText = "@" + op.insert.mention.value;
        mentions.push({
          id: op.insert.mention.id,
          value: op.insert.mention.value,
          mentionText,
          start: cursor,
          end: cursor + mentionText.length
        });
        cursor += mentionText.length;
      }
    });

    setMentions(mentions);
  };

  // ✅ Enhanced file removal function for all file types
  const removeFileFromContent = (fileElement) => {
    const wrapper = fileElement.closest('.file-wrapper');
    if (wrapper) {
      wrapper.remove();
      const quill = quillRef.current?.getEditor();
      if (quill) {
        setEditorContent(quill.root.innerHTML);
      }
    }
  };

  const notifyError = (data) => {

    toast.error(`❌ ${data}`, {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
    });
  };

  // Loading spinner component
  const LoadingSpinner = ({ size = "sm" }) => {
    const sizeClasses = {
      sm: "w-4 h-4",
      md: "w-6 h-6",
      lg: "w-8 h-8"
    };
    
    return (
      <div className={`${sizeClasses[size]} animate-spin rounded-full border-2 border-gray-300 border-t-blue-600`}></div>
    );
  };

  const handleDeleteWithConfirmation = async (commentid, file) => {
    const willDelete = await swal({
      title: "Are you sure?",
      text: "Once deleted, you will not be able to recover this file",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    });

    if (willDelete) {
      setIsDeletingFile(true);
      try {
        let result = await axiosInstance.post("/task/deletecommentfile", {
          commentId: commentid,
          fileId: file
        });

        if (result) {
          swal("Deleted successfully!", {
            icon: "success",
          });
          setCombinedComments(prev =>
            prev.map(comment =>
              comment.commentId === commentid
                ? { ...comment, files: comment.files.filter(f => f.id !== file) }
                : comment
            )
          );
        } else {
          notifyError("Error in deleting");
        }
      } catch (err) {
        notifyError("An unexpected error occurred");
        console.error(err);
      } finally {
        setIsDeletingFile(false);
      }
    } else {
      swal("Project is safe!");
    }
  };

  // Recursive component to render comments and their replies
  const renderComment = (comment, level = 0, isLast = false) => {
    const isEdited = comment.updatedAt && comment.createdAt && comment.updatedAt !== comment.createdAt;
    return (
      <div key={comment.commentId} id={`comment-${comment.commentId}`} className="comment-group-wrapper" style={{ marginBottom: isLast ? '0' : '24px' }}>
        <div className="comment-item">
          {/* Avatar */}
          <div className="comment-avatar">
            {comment?.userName?.charAt(0).toUpperCase()}
          </div>

          {/* Content */}
          <div className="comment-body">
            <div className="comment-header">
              <span className="comment-username">{comment.userName}</span>
              <span className="comment-timestamp">{comment.createdAt}</span>
              {isEdited && <span className="comment-edited">Edited {comment.updatedAt}</span>}
            </div>

            {/* Display subject only when user entered one (avoid duplicate with content-derived display) */}
            {comment.originalSubject && (
              <div className="comment-subject mb-1" style={{ fontWeight: 600, color: '#333', fontSize: '0.95rem' }}>
                {comment.originalSubject}
              </div>
            )}

            {/* Display content - plain text style, no background box */}
            <div className="comment-content" dangerouslySetInnerHTML={{ __html: comment.content }}></div>

            {/* Display files */}
            {comment.files && comment.files.length > 0 && (
              <div className="mt-2 mb-2 flex flex-wrap gap-2">
                {comment.files.map((file, idx) => {
                  const fileExtension = (file.file_extension || '').toLowerCase();
                  const isImage = fileExtension.match(/^(png|jpg|jpeg|gif|webp|svg)$/);
                  const openFile = async () => {
                    if (!file.id) {
                      toast.error('Attachment is missing an id. Refresh the page.');
                      return;
                    }
                    try {
                      await openTaskAttachmentInNewTab(file.id);
                    } catch (e) {
                      toast.error(e.message || 'Could not open file');
                    }
                  };

                  if (isImage) {
                    return (
                      <div
                        key={idx}
                        className="file-attachment-card"
                        style={{
                          position: 'relative',
                          borderRadius: '8px',
                          overflow: 'hidden',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                          transition: 'transform 0.2s, box-shadow 0.2s',
                          cursor: file.id ? 'pointer' : 'default',
                          border: '1px solid #e0e0e0',
                          background: '#fff'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'translateY(-2px)';
                          e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                        }}
                        onClick={openFile}
                      >
                        {file.id ? (
                        <TaskAttachmentImage
                          fileId={file.id}
                          alt={file.file_name}
                          style={{
                            width: '100px',
                            height: '100px',
                            objectFit: 'cover',
                            display: 'block'
                          }}
                        />
                        ) : (
                          <div style={{ width: 100, height: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: '#888' }}>Refresh</div>
                        )}
                        <div
                          style={{
                            padding: '4px 6px',
                            background: 'rgba(255,255,255,0.95)',
                            borderTop: '1px solid #e0e0e0',
                            fontSize: '10px',
                            color: '#555',
                            fontWeight: '500',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}
                          title={file.file_name}
                        >
                          {file.file_name.length > 13 ? file.file_name.substring(0, 13) + '...' : file.file_name}
                        </div>
                      </div>
                    );
                  }

                  // Non-image files
                  const getFileIcon = (ext) => {
                    const iconMap = {
                      pdf: '📄',
                      doc: '📝',
                      docx: '📝',
                      xls: '📊',
                      xlsx: '📊',
                      ppt: '📊',
                      pptx: '📊',
                      txt: '📃',
                      zip: '🗜️',
                      rar: '🗜️',
                      mp4: '🎥',
                      mp3: '🎵',
                      csv: '📋'
                    };
                    return iconMap[ext] || '📎';
                  };

                  return (
                    <a
                      key={idx}
                      href="#"
                      className="file-attachment-card"
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '100px',
                        height: '100px',
                        padding: '12px',
                        border: '1px solid #e0e0e0',
                        borderRadius: '8px',
                        background: '#f8f9fa',
                        textDecoration: 'none',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        transition: 'transform 0.2s, box-shadow 0.2s, background 0.2s',
                        cursor: 'pointer',
                        textAlign: 'center'
                      }}
                      onClick={async (e) => {
                        e.preventDefault();
                        await openFile();
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                        e.currentTarget.style.background = '#e8f4f8';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                        e.currentTarget.style.background = '#f8f9fa';
                      }}
                    >
                      <div
                        style={{
                          fontSize: '28px',
                          marginBottom: '6px'
                        }}
                      >
                        {getFileIcon(fileExtension)}
                      </div>
                      <div
                        style={{
                          fontSize: '10px',
                          color: '#333',
                          fontWeight: '500',
                          wordBreak: 'break-word',
                          lineHeight: '1.2',
                          maxWidth: '100%'
                        }}
                        title={file.file_name}
                      >
                        {file.file_name.length > 14 ? file.file_name.substring(0, 14) + '...' : file.file_name}
                      </div>
                      <div
                        style={{
                          fontSize: '8px',
                          color: '#888',
                          marginTop: '3px',
                          textTransform: 'uppercase',
                          fontWeight: '600'
                        }}
                      >
                        {fileExtension}
                      </div>
                    </a>
                  );
                })}
              </div>
            )}

            {/* Action bar - Reply, Edit, etc. */}
            <div className="comment-actions-bar">
              <button
                type="button"
                className="comment-action-btn"
                onClick={() => handleReply(comment)}
                aria-label="Reply"
              >
                <i className="far fa-comment" aria-hidden></i>
                Reply
              </button>
              {currentUser && currentUser.id === comment.userId && (
                <button
                  type="button"
                  className="comment-action-btn"
                  onClick={() => handleEdit(comment)}
                  aria-label="Edit"
                >
                  <i className="far fa-edit" aria-hidden></i>
                  Edit
                </button>
              )}
              {currentUser && hasWorkRegisterAccess && (
                <button
                  type="button"
                  className="comment-action-btn"
                  onClick={() => setWrTagCommentId(comment.commentId)}
                  aria-label="Add to Work Register"
                  title="Add to Work Register"
                >
                  <i className="far fa-bell" aria-hidden></i>
                  Work Register
                </button>
              )}
            </div>

            {/* Reply editor inline */}
          {replyingTo === comment.commentId && mode === 'reply' && (
            <div className="mt-3 ml-0">
              <div className="border border-green-300 rounded p-2 bg-green-50">
                <input
                  ref={subjectInputRef}
                  type="text"
                  className="form-control mb-2"
                  placeholder="Subject (optional)"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  maxLength={500}
                  style={{ fontSize: '0.9rem' }}
                />
                <ReactQuill
                  ref={quillRef}
                  theme="snow"
                  value={editorContent}
                  onChange={handleChange}
                  modules={modules}
                  placeholder="Write a reply..."
                  className="mb-2"
                />
                {commentError && <div className="text-danger small mt-1 mb-2">{commentError}</div>}
                
                {/* File Upload Section */}
                <div className="file-upload-section mt-3 p-3 border border-gray-200 rounded-lg bg-gray-50">
                  <div className="flex items-center justify-between mb-2">
                    <button
                      type="button"
                      onClick={handleFileUpload}
                      disabled={isReplying}
                      className="inline-flex items-center gap-1 px-2 py-1 text-xs font-normal text-gray-500 bg-gray-100 border border-gray-200 rounded hover:bg-gray-200 hover:text-gray-600 focus:outline-none focus:ring-1 focus:ring-gray-400 transition-all duration-150"
                      style={{ 
                        opacity: isReplying ? 0.6 : 1, 
                        cursor: isReplying ? 'not-allowed' : 'pointer'
                      }}
                    >
                      {isReplying && <LoadingSpinner size="sm" />}
                      + Add Files
                    </button>
                  </div>
                  
                  {/* Display uploaded files */}
                  {uploadedFile && uploadedFile.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2 items-start" style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'flex-start' }}>
                      {uploadedFile.map((file, idx) => {
                        const fileUrl = URL.createObjectURL(file);
                        
                        if (file.type.startsWith('image/')) {
                          return (
                            <div key={idx} className="file-wrapper" style={{ position: 'relative', display: 'inline-flex', alignItems: 'flex-start' }}>
                              <img
                                src={fileUrl}
                                alt={file.name}
                                style={{ width: '100px', height: '100px', objectFit: 'cover', cursor: 'pointer' }}
                                className="rounded border"
                                onClick={() => window.open(fileUrl, '_blank')}
                              />
                              <button
                                className="delete-btn"
                                onClick={() => handleRemoveFile(idx)}
                                disabled={isReplying}
                                style={{
                                  position: 'absolute',
                                  top: '-8px',
                                  right: '-8px',
                                  background: isReplying ? '#ccc' : '#ff4444',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '50%',
                                  width: '20px',
                                  height: '20px',
                                  cursor: isReplying ? 'not-allowed' : 'pointer',
                                  fontSize: '12px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}
                              >
                                {isReplying ? <LoadingSpinner size="sm" /> : '✕'}
                              </button>
                            </div>
                          );
                        }

                        return (
                          <div key={idx} className="file-wrapper" style={{ position: 'relative', display: 'inline-flex', alignItems: 'flex-start' }}>
                            <a
                              href={fileUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="underline"
                              style={{ 
                                color: '#2A3335', 
                                textDecoration: 'underline',
                                padding: '8px',
                                border: '1px solid #ddd',
                                borderRadius: '4px',
                                background: '#f9f9f9',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                height: '100px',
                                width: '100px',
                                flexDirection: 'column',
                                justifyContent: 'center',
                                textAlign: 'center'
                              }}
                            >
                              <div 
                                style={{
                                  width: '32px',
                                  height: '32px',
                                  background: '#666',
                                  borderRadius: '4px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  color: 'white',
                                  fontSize: '16px',
                                  marginBottom: '4px'
                                }}
                              >
                                📄
                              </div>
                              <span style={{ fontSize: '10px', wordBreak: 'break-word', lineHeight: '1.2' }}>
                                {file.name.length > 15 ? file.name.substring(0, 15) + '...' : file.name}
                              </span>
                            </a>
                            <button
                              className="delete-btn"
                              onClick={() => handleRemoveFile(idx)}
                              disabled={isSubmitting}
                              style={{
                                position: 'absolute',
                                top: '-8px',
                                right: '-8px',
                                background: isSubmitting ? '#ccc' : '#ff4444',
                                color: 'white',
                                border: 'none',
                                borderRadius: '50%',
                                width: '20px',
                                height: '20px',
                                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                                fontSize: '12px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                            >
                              {isSubmitting ? <LoadingSpinner size="sm" /> : '✕'}
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
                
                <div className="flex gap-2 items-center" style={{ display: 'flex', marginTop: '20px' }}>
                  <button 
                    className="submit-button" 
                    onClick={handleSubmit}
                    disabled={isReplying}
                    style={{ 
                      opacity: isReplying ? 0.6 : 1, 
                      cursor: isReplying ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      minWidth: '120px',
                      justifyContent: 'center'
                    }}
                  >
                    {isReplying && <LoadingSpinner size="sm" />}
                    {isReplying ? 'Submitting...' : 'Submit Reply'}
                  </button>
                  <button 
                    className="cancel-button" 
                    onClick={handleCancel}
                    disabled={isReplying}
                    style={{ 
                      opacity: isReplying ? 0.6 : 1, 
                      cursor: isReplying ? 'not-allowed' : 'pointer',
                      minWidth: '80px'
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Render replies with vertical connector */}
          {comment.replies && comment.replies.length > 0 && (
            <div className="comment-replies-wrapper">
              {comment.replies.map((reply) => {
                const replyIsEdited = reply.updatedAt && reply.createdAt && reply.updatedAt !== reply.createdAt;
                return (
                  <div key={reply.commentId} id={`comment-${reply.commentId}`} className="comment-reply-item">
                    <div className="comment-avatar">
                      {reply?.userName?.charAt(0).toUpperCase()}
                    </div>

                    <div className="comment-body">
                      <div className="comment-header">
                        <span className="comment-username">{reply.userName}</span>
                        <span className="comment-timestamp">{reply.createdAt}</span>
                        {replyIsEdited && <span className="comment-edited">Edited {reply.updatedAt}</span>}
                      </div>

                      {reply.originalSubject && (
                        <div className="comment-subject mb-1" style={{ fontWeight: 600, color: '#333', fontSize: '0.95rem' }}>
                          {reply.originalSubject}
                        </div>
                      )}

                      <div className="comment-content" dangerouslySetInnerHTML={{ __html: reply.content }} />

                    {/* Display files for replies */}
                    {reply.files && reply.files.length > 0 && (
                      <div className="mt-2 mb-2 flex flex-wrap gap-2">
                        {reply.files.map((file, idx) => {
                          const fileExtension = (file.file_extension || '').toLowerCase();
                          const isImage = fileExtension.match(/^(png|jpg|jpeg|gif|webp|svg)$/);
                          const openFile = async () => {
                            if (!file.id) {
                              toast.error('Attachment is missing an id. Refresh the page.');
                              return;
                            }
                            try {
                              await openTaskAttachmentInNewTab(file.id);
                            } catch (e) {
                              toast.error(e.message || 'Could not open file');
                            }
                          };

                          if (isImage) {
                            return (
                              <div
                                key={idx}
                                className="file-attachment-card"
                                style={{
                                  position: 'relative',
                                  borderRadius: '8px',
                                  overflow: 'hidden',
                                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                  transition: 'transform 0.2s, box-shadow 0.2s',
                                  cursor: file.id ? 'pointer' : 'default',
                                  border: '1px solid #e0e0e0',
                                  background: '#fff'
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.transform = 'translateY(-2px)';
                                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.transform = 'translateY(0)';
                                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                                }}
                                onClick={openFile}
                              >
                                {file.id ? (
                                <TaskAttachmentImage
                                  fileId={file.id}
                                  alt={file.file_name}
                                  style={{
                                    width: '100px',
                                    height: '100px',
                                    objectFit: 'cover',
                                    display: 'block'
                                  }}
                                />
                                ) : (
                                  <div style={{ width: 100, height: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: '#888' }}>Refresh</div>
                                )}
                                <div
                                  style={{
                                    padding: '4px 6px',
                                    background: 'rgba(255,255,255,0.95)',
                                    borderTop: '1px solid #e0e0e0',
                                    fontSize: '10px',
                                    color: '#555',
                                    fontWeight: '500',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis'
                                  }}
                                  title={file.file_name}
                                >
                                  {file.file_name.length > 13 ? file.file_name.substring(0, 13) + '...' : file.file_name}
                                </div>
                              </div>
                            );
                          }

                          // Non-image files
                          const getFileIcon = (ext) => {
                            const iconMap = {
                              pdf: '📄',
                              doc: '📝',
                              docx: '📝',
                              xls: '📊',
                              xlsx: '📊',
                              ppt: '📊',
                              pptx: '📊',
                              txt: '📃',
                              zip: '🗜️',
                              rar: '🗜️',
                              mp4: '🎥',
                              mp3: '🎵',
                              csv: '📋'
                            };
                            return iconMap[ext] || '📎';
                          };

                          return (
                            <a
                              key={idx}
                              href="#"
                              className="file-attachment-card"
                              style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '100px',
                                height: '100px',
                                padding: '12px',
                                border: '1px solid #e0e0e0',
                                borderRadius: '8px',
                                background: '#f8f9fa',
                                textDecoration: 'none',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                transition: 'transform 0.2s, box-shadow 0.2s, background 0.2s',
                                cursor: 'pointer',
                                textAlign: 'center'
                              }}
                              onClick={async (e) => {
                                e.preventDefault();
                                await openFile();
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'translateY(-2px)';
                                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                                e.currentTarget.style.background = '#e8f4f8';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                                e.currentTarget.style.background = '#f8f9fa';
                              }}
                            >
                              <div
                                style={{
                                  fontSize: '28px',
                                  marginBottom: '6px'
                                }}
                              >
                                {getFileIcon(fileExtension)}
                              </div>
                              <div
                                style={{
                                  fontSize: '10px',
                                  color: '#333',
                                  fontWeight: '500',
                                  wordBreak: 'break-word',
                                  lineHeight: '1.2',
                                  maxWidth: '100%'
                                }}
                                title={file.file_name}
                              >
                                {file.file_name.length > 14 ? file.file_name.substring(0, 14) + '...' : file.file_name}
                              </div>
                              <div
                                style={{
                                  fontSize: '8px',
                                  color: '#888',
                                  marginTop: '3px',
                                  textTransform: 'uppercase',
                                  fontWeight: '600'
                                }}
                              >
                                {fileExtension}
                              </div>
                            </a>
                          );
                        })}
                      </div>
                    )}

                      <div className="comment-actions-bar">
                        {currentUser && currentUser.id === reply.userId && (
                          <button
                            type="button"
                            className="comment-action-btn"
                            onClick={() => handleEdit(reply)}
                            aria-label="Edit"
                          >
                            <i className="far fa-edit" aria-hidden></i>
                            Edit
                          </button>
                        )}
                        {currentUser && hasWorkRegisterAccess && (
                          <button
                            type="button"
                            className="comment-action-btn"
                            onClick={() => setWrTagCommentId(reply.commentId)}
                            aria-label="Add to Work Register"
                            title="Add to Work Register"
                          >
                            <i className="far fa-bell" aria-hidden></i>
                            Work Register
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        </div>
        
        {/* Subtle spacing between comment groups */}
        {!isLast && <div style={{ height: '8px' }} />}
      </div>
    );
  };

  const containerRef = useRef(null);

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.focus();
      containerRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, []);

  return (
    <>
      <div className="comment-thread space-y-4">
        {/* Add comment - at top */}
        {!expanded && !replyingTo ? (
          <div
            className="comment-add-placeholder"
            onClick={handleAddComment}
          >
            Add a comment...
          </div>
        ) : null}

        {/* New comment editor - at top when adding */}
        {expanded && activeCommentId == null && !replyingTo ? (
          <div className="mb-4">
            <div className="border border-gray-300 rounded p-2">
              <input
                ref={subjectInputRef}
                type="text"
                className="form-control mb-2"
                placeholder="Transaction statement / Subject (optional)"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                maxLength={500}
                style={{ fontSize: '0.9rem' }}
              />
              <ReactQuill
                ref={quillRef}
                theme="snow"
                value={editorContent}
                onChange={handleChange}
                modules={modules}
                placeholder="Comment..."
                className="mb-2"
              />
              {commentError && <div className="text-danger small mt-1 mb-2">{commentError}</div>}
              
              {/* File Upload Section */}
              <div className="file-upload-section mt-3 p-3 border border-gray-200 rounded-lg bg-gray-50">
                <div className="flex items-center justify-between mb-2">
                  <button
                    type="button"
                    onClick={handleFileUpload}
                    disabled={isSubmitting}
                    className="inline-flex items-center gap-1 px-2 py-1 text-xs font-normal text-gray-500 bg-gray-100 border border-gray-200 rounded hover:bg-gray-200 hover:text-gray-600 focus:outline-none focus:ring-1 focus:ring-gray-400 transition-all duration-150"
                    style={{ 
                      opacity: isSubmitting ? 0.6 : 1, 
                      cursor: isSubmitting ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {isSubmitting && <LoadingSpinner size="sm" />}
                    + Add Files
                  </button>
                </div>
                
                {/* Display uploaded files */}
                {uploadedFile && uploadedFile.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2 items-start" style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'flex-start' }}>
                    {uploadedFile.map((file, idx) => {
                      const fileUrl = URL.createObjectURL(file);
                      
                      if (file.type.startsWith('image/')) {
                        return (
                          <div key={idx} className="file-wrapper" style={{ position: 'relative', display: 'inline-flex', alignItems: 'flex-start' }}>
                            <img
                              src={fileUrl}
                              alt={file.name}
                              style={{ width: '100px', height: '100px', objectFit: 'cover', cursor: 'pointer' }}
                              className="rounded border"
                              onClick={() => window.open(fileUrl, '_blank')}
                            />
                            <button
                              className="delete-btn"
                              onClick={() => handleRemoveFile(idx)}
                              style={{
                                position: 'absolute',
                                top: '-8px',
                                right: '-8px',
                                background: '#ff4444',
                                color: 'white',
                                border: 'none',
                                borderRadius: '50%',
                                width: '20px',
                                height: '20px',
                                cursor: 'pointer',
                                fontSize: '12px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              ✕
                            </button>
                          </div>
                        );
                      }

                      return (
                        <div key={idx} className="file-wrapper" style={{ position: 'relative', display: 'inline-flex', alignItems: 'flex-start' }}>
                          <a
                            href={fileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="underline"
                            style={{ 
                              color: '#2A3335', 
                              textDecoration: 'underline',
                              padding: '8px',
                              border: '1px solid #ddd',
                              borderRadius: '4px',
                              background: '#f9f9f9',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              height: '100px',
                              width: '100px',
                              flexDirection: 'column',
                              justifyContent: 'center',
                              textAlign: 'center'
                            }}
                          >
                            <div 
                              style={{
                                width: '32px',
                                height: '32px',
                                background: '#666',
                                borderRadius: '4px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'white',
                                fontSize: '16px',
                                marginBottom: '4px'
                              }}
                            >
                              📄
                            </div>
                            <span style={{ fontSize: '10px', wordBreak: 'break-word', lineHeight: '1.2' }}>
                              {file.name.length > 15 ? file.name.substring(0, 15) + '...' : file.name}
                            </span>
                          </a>
                          <button
                            className="delete-btn"
                            onClick={() => handleRemoveFile(idx)}
                            disabled={isSubmitting}
                            style={{
                              position: 'absolute',
                              top: '-8px',
                              right: '-8px',
                              background: isSubmitting ? '#ccc' : '#ff4444',
                              color: 'white',
                              border: 'none',
                              borderRadius: '50%',
                              width: '20px',
                              height: '20px',
                              cursor: isSubmitting ? 'not-allowed' : 'pointer',
                              fontSize: '12px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            {isSubmitting ? <LoadingSpinner size="sm" /> : '✕'}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              
              <div className="flex gap-2 items-center" style={{ display: 'flex', marginTop: '20px' }}>
                <button 
                  className="submit-button" 
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  style={{ 
                    opacity: isSubmitting ? 0.6 : 1, 
                    cursor: isSubmitting ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    minWidth: '100px',
                    justifyContent: 'center'
                  }}
                >
                  {isSubmitting && <LoadingSpinner size="sm" />}
                  {isSubmitting ? 'Submitting...' : 'Submit'}
                </button>
                <button 
                  className="cancel-button" 
                  onClick={handleCancel}
                  disabled={isSubmitting}
                  style={{ 
                    opacity: isSubmitting ? 0.6 : 1, 
                    cursor: isSubmitting ? 'not-allowed' : 'pointer',
                    minWidth: '80px'
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        ) : null}

        {/* Existing Comments */}
        <div className="comment-list space-y-4">
          {combinedComments.map((comment, index) =>
            (expanded && mode === 'edit' && (editingCommentId === comment.commentId)) ? (
              <div
                key={comment.commentId}
                ref={containerRef}
                tabIndex={-1}
                className="border border-gray-300 rounded p-2 mt-4 outline-none focus:ring-2 focus:ring-blue-300"
              >
                <input
                  ref={subjectInputRef}
                  type="text"
                  className="form-control mb-2"
                  placeholder={comment.parentId ? "Subject (optional)" : "Daily update / Subject (optional)"}
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  maxLength={500}
                  style={{ fontSize: '0.9rem' }}
                />
                <ReactQuill
                  ref={quillRef}
                  theme="snow"
                  value={editorContent}
                  onChange={handleChange}
                  modules={modules}
                  placeholder="Write your comment..."
                  className="mb-2"
                />
                {commentError && <div className="text-danger small mt-1 mb-2">{commentError}</div>}
                
                {/* File Upload Section */}
                <div className="file-upload-section mt-3 p-3 border border-gray-200 rounded-lg bg-gray-50">
                  <div className="flex items-center justify-between mb-2">
                    <button
                      type="button"
                      onClick={handleFileUpload}
                      disabled={isEditing}
                      className="inline-flex items-center gap-1 px-2 py-1 text-xs font-normal text-gray-500 bg-gray-100 border border-gray-200 rounded hover:bg-gray-200 hover:text-gray-600 focus:outline-none focus:ring-1 focus:ring-gray-400 transition-all duration-150"
                      style={{ 
                        opacity: isEditing ? 0.6 : 1, 
                        cursor: isEditing ? 'not-allowed' : 'pointer'
                      }}
                    >
                      {isEditing && <LoadingSpinner size="sm" />}
                      + Add Files
                    </button>
                  </div>
                  
                  {/* Display uploaded files */}
                  {uploadedFile && uploadedFile.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2 items-start" style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'flex-start' }}>
                      {uploadedFile.map((file, idx) => {
                        // Check if it's an existing file (has id) or new file (File object)
                        const isExistingFile = file.id !== undefined;
                        const blobUrl = !isExistingFile ? URL.createObjectURL(file) : null;
                        const fileName = isExistingFile ? file.file_name : file.name;
                        const isImage = isExistingFile
                          ? (file.file_extension || '').toLowerCase().match(/^(png|jpg|jpeg|gif|webp)$/)
                          : file.type.startsWith('image/');

                        const openExisting = async () => {
                          if (!file.id) return;
                          try {
                            await openTaskAttachmentInNewTab(file.id);
                          } catch (e) {
                            toast.error(e.message || 'Could not open file');
                          }
                        };

                        if (isImage) {
                          return (
                            <div key={isExistingFile ? file.id : idx} className="file-wrapper" style={{ position: 'relative', display: 'inline-flex', alignItems: 'flex-start' }}>
                              {isExistingFile && file.id ? (
                                <TaskAttachmentImage
                                  fileId={file.id}
                                  alt={fileName}
                                  style={{ width: '100px', height: '100px', objectFit: 'cover', cursor: 'pointer' }}
                                  className="rounded border"
                                  onClick={openExisting}
                                />
                              ) : (
                              <img
                                src={blobUrl}
                                alt={fileName}
                                style={{ width: '100px', height: '100px', objectFit: 'cover', cursor: 'pointer' }}
                                className="rounded border"
                                onClick={() => window.open(blobUrl, '_blank')}
                              />
                              )}
                              <button
                                className="delete-btn"
                                onClick={() => isExistingFile 
                                  ? handleRemoveExistingFile(file.id, fileName)
                                  : handleRemoveFile(idx)
                                }
                                disabled={isDeletingFile}
                                style={{
                                  position: 'absolute',
                                  top: '-8px',
                                  right: '-8px',
                                  background: isDeletingFile ? '#ccc' : '#ff4444',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '50%',
                                  width: '20px',
                                  height: '20px',
                                  cursor: isDeletingFile ? 'not-allowed' : 'pointer',
                                  fontSize: '12px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}
                              >
                                {isDeletingFile ? <LoadingSpinner size="sm" /> : '✕'}
                              </button>
                            </div>
                          );
                        }

                        return (
                          <div key={isExistingFile ? file.id : idx} className="file-wrapper" style={{ position: 'relative', display: 'inline-flex', alignItems: 'flex-start' }}>
                            <a
                              href={isExistingFile ? '#' : blobUrl}
                              target={isExistingFile ? undefined : '_blank'}
                              rel={isExistingFile ? undefined : 'noopener noreferrer'}
                              className="underline"
                              style={{ 
                                color: '#2A3335', 
                                textDecoration: 'underline',
                                padding: '8px',
                                border: '1px solid #ddd',
                                borderRadius: '4px',
                                background: '#f9f9f9',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                height: '100px',
                                width: '100px',
                                flexDirection: 'column',
                                justifyContent: 'center',
                                textAlign: 'center'
                              }}
                              onClick={isExistingFile ? async (e) => { e.preventDefault(); await openExisting(); } : undefined}
                            >
                              <div 
                                style={{
                                  width: '32px',
                                  height: '32px',
                                  background: '#666',
                                  borderRadius: '4px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  color: 'white',
                                  fontSize: '16px',
                                  marginBottom: '4px'
                                }}
                              >
                                📄
                              </div>
                              <span style={{ fontSize: '10px', wordBreak: 'break-word', lineHeight: '1.2' }}>
                                {fileName.length > 15 ? fileName.substring(0, 15) + '...' : fileName}
                              </span>
                            </a>
                            <button
                              className="delete-btn"
                              onClick={() => isExistingFile 
                                ? handleRemoveExistingFile(file.id, fileName)
                                : handleRemoveFile(idx)
                              }
                              disabled={isDeletingFile}
                              style={{
                                position: 'absolute',
                                top: '-8px',
                                right: '-8px',
                                background: isDeletingFile ? '#ccc' : '#ff4444',
                                color: 'white',
                                border: 'none',
                                borderRadius: '50%',
                                width: '20px',
                                height: '20px',
                                cursor: isDeletingFile ? 'not-allowed' : 'pointer',
                                fontSize: '12px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                            >
                              {isDeletingFile ? <LoadingSpinner size="sm" /> : '✕'}
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
                
                <div className="flex gap-2 items-center" style={{ display: 'flex', marginTop: '20px' }}>
                  <button 
                    className="submit-button" 
                    onClick={handleSubmit}
                    disabled={isEditing}
                    style={{ 
                      opacity: isEditing ? 0.6 : 1, 
                      cursor: isEditing ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      minWidth: '100px',
                      justifyContent: 'center'
                    }}
                  >
                    {isEditing && <LoadingSpinner size="sm" />}
                    {isEditing ? 'Updating...' : 'Submit'}
                  </button>
                  <button 
                    className="cancel-button" 
                    onClick={handleCancel}
                    disabled={isEditing}
                    style={{ 
                      opacity: isEditing ? 0.6 : 1, 
                      cursor: isEditing ? 'not-allowed' : 'pointer',
                      minWidth: '80px'
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              renderComment(comment, 0, index === combinedComments.length - 1)
            )
          )}
        </div>

      </div>

      {hasWorkRegisterAccess && (
        <WorkRegisterTagModal
          show={!!wrTagCommentId}
          commentId={wrTagCommentId}
          onHide={() => setWrTagCommentId(null)}
        />
      )}
    </>
  )
}